import { useEffect, useState } from 'react';
import { Card, Col, Empty, Input, Row, Space, Statistic, Table, Tooltip, Typography, type TableColumnsType } from 'antd';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { ListenerName } from '../../lib/listenerName';
import { formatCoins, formatRupees } from '../../lib/money';
import { formatDuration, type ListenerRowStats, type ListenerSummary, type Paged } from '../listeners/listenerStats';
import { StatsRangeFilter, useUrlStatsRange } from '../listeners/ListenerStatsWidgets';

/** The slice of `GET /admin/listeners` this page reads. */
interface CallerCoinsRow {
  id: string;
  userId: string;
  displayName: string;
  user: { handle: string };
  stats: ListenerRowStats;
}

/**
 * Caller coins — the coins callers spent on each listener's calls (net of
 * refunds) for the selected range. Split out of the Listeners page so that page
 * is only listener management. Reads the same API (`/admin/listeners` and its
 * summary); `money` is null there without `listeners.earnings.view`.
 */
export function CallerCoinsPage() {
  const { preset, setPreset, setCustom, range } = useUrlStatsRange('today');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => setPage(1), [q, range]);

  const list = useQuery({
    queryKey: ['admin', 'caller-coins', q, page, pageSize, range],
    queryFn: async () =>
      (
        await api.get<Paged<CallerCoinsRow>>('/admin/listeners', {
          params: { q: q || undefined, page, pageSize, ...range },
        })
      ).data,
    enabled: range !== null,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const summary = useQuery({
    queryKey: ['admin', 'caller-coins', 'summary', q, range],
    queryFn: async () =>
      (await api.get<ListenerSummary>('/admin/listeners/summary', { params: { q: q || undefined, ...range } })).data,
    enabled: range !== null,
    refetchInterval: 60_000,
  });

  const columns: TableColumnsType<CallerCoinsRow> = [
    {
      title: 'Listener',
      render: (_: unknown, row) => (
        <ListenerName
          listener={{ displayName: row.displayName, handle: row.user.handle, id: row.id }}
          listenerId={row.id}
          tab="earnings"
        />
      ),
    },
    {
      title: <Tooltip title="Connected calls / all call attempts">Calls</Tooltip>,
      render: (_: unknown, row) => `${row.stats.calls.connectedCalls} / ${row.stats.calls.calls}`,
    },
    {
      title: <Tooltip title="Connected talk time of calls started in the selected range">Call time</Tooltip>,
      render: (_: unknown, row) => formatDuration(row.stats.calls.callSeconds),
    },
    {
      title: <Tooltip title="Coins callers spent on this listener's calls, net of refunds">Caller coins</Tooltip>,
      render: (_: unknown, row) => (row.stats.money ? formatCoins(row.stats.money.callerCoins) : '—'),
    },
    {
      title: <Tooltip title="₹ the listener earned from these calls (listeners earn ₹, not coins)">Call earnings</Tooltip>,
      render: (_: unknown, row) => formatRupees(row.stats.money?.callEarningsPaise),
    },
  ];

  const sum = summary.data;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Coins spent per listener
        </Typography.Title>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Listener name, @handle, phone last-4 or id"
            onSearch={(v) => setQ(v.trim())}
            style={{ width: 280 }}
          />
          <StatsRangeFilter preset={preset} setPreset={setPreset} setCustom={setCustom} />
        </Space>
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={8}>
          <Card size="small" loading={summary.isLoading}>
            <Statistic
              title={<Tooltip title="Coins callers spent on listener calls in the range (net of refunds)">Caller coins spent</Tooltip>}
              value={sum?.callerCoins === null || sum?.callerCoins === undefined ? '—' : formatCoins(sum.callerCoins)}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small" loading={summary.isLoading}>
            <Statistic title="Connected calls" value={sum ? `${sum.connectedCalls} / ${sum.calls}` : '—'} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small" loading={summary.isLoading}>
            <Statistic title="Call time" value={formatDuration(sum?.callSeconds)} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          rowKey="id"
          loading={list.isLoading}
          dataSource={list.data?.items ?? []}
          columns={columns}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description="No listener calls in this range" /> }}
          pagination={{
            current: page,
            pageSize,
            total: list.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100],
            showTotal: (t) => `${t.toLocaleString('en-IN')} listeners`,
            onChange: (p, size) => {
              setPage(size !== pageSize ? 1 : p);
              setPageSize(size);
            },
          }}
        />
      </Card>
    </Space>
  );
}
